import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  EnvelopeSimple, ChartLineUp, Warning, Sparkle, CheckCircle,
} from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * DigestPreviewCard — Overview widget showing "what would go in
 * Monday's coaching digest".
 *
 * Uses the same aggregator the Monday email uses so what the owner
 * sees here matches what lands in their inbox exactly. Auto-hides
 * when there's zero activity (silent for fresh fleets).
 */
export default function DigestPreviewCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: d } = await client.get("/coaching-digest/preview");
        if (alive) setData(d);
      } catch (e) {
        if (alive) toast.error(e?.response?.data?.detail || "Digest preview failed");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) return null;
  if (!data) return null;

  const s = data.stats || {};
  const total = s.total_activity || 0;

  // Silent for fresh fleets — owner shouldn't see an empty widget
  if (total === 0 && !data.already_sent_this_week) return null;

  const alreadySent = data.already_sent_this_week;
  const sentAt = data.sent_at ? new Date(data.sent_at) : null;

  return (
    <div
      className="card-surface p-5"
      data-testid="digest-preview-card"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 inline-flex items-center gap-1.5">
            <EnvelopeSimple size={11} weight="fill" className="text-[#007AFF]" />
            Monday coaching digest
          </div>
          <div className="font-display text-xl font-semibold text-white">
            This week at a glance
          </div>
          <div className="text-xs text-neutral-500 mt-1">
            {alreadySent
              ? <>Sent <b className="text-emerald-400">{sentAt?.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</b> · these are the numbers that landed in your inbox</>
              : <>What will land Monday 8am — grounded on this past 7 days</>
            }
          </div>
        </div>
        {alreadySent && (
          <div
            className="pill pill-emerald inline-flex items-center gap-1 shrink-0"
            data-testid="digest-preview-sent-badge"
          >
            <CheckCircle size={10} weight="fill" />
            Sent
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Stat label="Coached" value={s.coached_this_week} color="text-white" testid="digest-preview-coached" />
        <Stat label="New this wk" value={s.new_this_week} color="text-amber-400" testid="digest-preview-new" />
        <Stat label="Streaks broken" value={s.streaks_broken} color="text-emerald-400" testid="digest-preview-broken" />
        <Stat label="You reset" value={s.resets_this_week} color="text-[#FF5A1F]" testid="digest-preview-reset" />
        <Stat label="Owner alerts" value={s.escalations_this_week} color="text-red-400" testid="digest-preview-escalated" />
      </div>

      {s.escalations_this_week > 0 && (
        <div className="mt-4 rounded-lg bg-red-500/[0.08] border border-red-500/30 p-3 text-xs text-red-200 inline-flex items-start gap-2"
             data-testid="digest-preview-escalation-flag">
          <Warning size={13} weight="fill" className="text-red-400 mt-0.5 shrink-0" />
          <span>
            <b>{s.escalations_this_week}</b> escalation
            {s.escalations_this_week === 1 ? "" : "s"} fired this week. Driver(s) hit 3+ weeks under 70% on-time — worth a face-to-face.
          </span>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color, testid }) {
  return (
    <div
      className="rounded-lg bg-white/[0.03] border border-white/5 p-3 text-center"
      data-testid={testid}
    >
      <div className={`text-2xl font-mono font-bold ${color} tabular-nums leading-none`}>
        {value ?? 0}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1.5 leading-tight">
        {label}
      </div>
    </div>
  );
}
